import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'select',
    'field',
    'stepSection',
    'stepTitle',
    'sectionTitle',
    'sectionHint',
    'sectionHeading',
    'variantLabel',
    'brandChipRow',
    'tagChipRow',
    'emptyState',
    'variantSectionTitle',
    'variantSectionHint',
    'variantSectionBadge',
    'variantAddLabel',
    'pricingSection',
    'pricingStepHint',
    'salePriceLabel',
    'salePriceHint',
    'stockQuantityField',
    'variantStockNote'
  ]

  static values = {
    slugMap: Object,
    hints: Object,
    tagChips: Object,
    variantPlaceholders: Object,
    fieldLabels: Object,
    fieldSlugs: Object,
    stepTitles: Object,
    variantConfig: Object,
    variantSections: Object
  }

  connect () {
    this.update()
  }

  update () {
    const slug = this.currentSlug()

    this.fieldTargets.forEach((field) => {
      const key = field.dataset.fieldKey
      const visible = this.isFieldVisible(key, slug)
      field.classList.toggle('hidden', !visible)

      const label = field.querySelector('[data-field-label]')
      if (label) {
        label.textContent = this.labelFor(key, slug, label.dataset.defaultLabel)
      }

      const caption = field.querySelector('[data-field-caption]')
      if (caption) {
        caption.textContent = this.labelFor(key, slug, caption.dataset.defaultLabel)
      }
    })

    this.updateVariantColumns(slug)

    this.stepSectionTargets.forEach((section) => {
      const keys = (section.dataset.stepKeys || '').split(',').filter(Boolean)
      const anyVisible = keys.some((key) => this.isFieldVisible(key, slug))
      section.classList.toggle('hidden', !anyVisible)
    })

    this.stepTitleTargets.forEach((title) => {
      const step = title.dataset.step
      const titles = this.stepTitlesValue[step] || {}
      title.textContent = titles[slug] || titles.default || title.dataset.defaultTitle || ''
    })

    const hint = slug
      ? (this.hintsValue[slug] || this.hintsValue.general || {})
      : {
          title: 'Product attributes',
          hint: 'Pick a category above to unlock tailored fields for that product type.'
        }

    if (this.hasSectionTitleTarget) {
      this.sectionTitleTarget.textContent = hint.title || 'Product attributes'
    }
    if (this.hasSectionHintTarget) {
      this.sectionHintTarget.textContent = hint.hint || ''
    }
    if (this.hasSectionHeadingTarget) {
      const titles = this.stepTitlesValue.attributes || {}
      this.sectionHeadingTarget.textContent = titles[slug] || titles.default || 'Step 4 · Category attributes'
    }

    if (this.hasVariantLabelTarget && slug) {
      const placeholder = this.variantPlaceholdersValue[slug] ||
        this.variantPlaceholdersValue.default ||
        this.variantPlaceholdersValue.general
      if (placeholder) this.variantLabelTarget.placeholder = placeholder
    }

    const variantMeta = (slug && this.variantSectionsValue[slug]) || {}
    if (this.hasVariantSectionTitleTarget) {
      this.variantSectionTitleTarget.textContent = variantMeta.title || 'Options & stock'
    }
    if (this.hasVariantSectionHintTarget) {
      this.variantSectionHintTarget.textContent = variantMeta.hint ||
        'Add each option with its own stock. Buyers pick one before ordering.'
    }
    if (this.hasVariantSectionBadgeTarget) {
      this.variantSectionBadgeTarget.textContent = variantMeta.badge || 'Options'
    }
    if (this.hasVariantAddLabelTarget) {
      this.variantAddLabelTarget.textContent = variantMeta.add_label || 'Add option'
    }

    this.updatePricingSection(slug)

    this.brandChipRowTargets.forEach((row) => {
      const chipSlug = slug || 'general'
      row.classList.toggle('hidden', row.dataset.slug !== chipSlug)
    })

    this.tagChipRowTargets.forEach((row) => {
      const chipSlug = slug || 'general'
      row.classList.toggle('hidden', row.dataset.slug !== chipSlug)
    })

    if (this.hasEmptyStateTarget) {
      const attributeKeys = [
        'storage_gb', 'ram_gb', 'battery_health', 'network_type', 'size', 'color',
        'material', 'serial_number', 'variant_code', 'warranty_months',
        'vehicle_make', 'vehicle_model', 'year_range', 'fabrication_type', 'variant_options'
      ]
      const anyAttributeVisible = attributeKeys.some((key) => this.isFieldVisible(key, slug))
      this.emptyStateTarget.classList.toggle('hidden', anyAttributeVisible)
    }

    this.dispatchVariantUpdate(slug)
  }

  dispatchVariantUpdate (slug) {
    const config = (slug && this.variantConfigValue[slug]) || { sizes: false, colors: false }
    document.dispatchEvent(new CustomEvent('product-category-fields:updated', {
      detail: { slug: slug || '', config },
      bubbles: true
    }))
  }

  updateVariantColumns (slug) {
    const cfg = (slug && this.variantConfigValue[slug]) || { sizes: false, colors: false }

    this.element.querySelectorAll('[data-variant-size-column]').forEach((col) => {
      col.classList.toggle('hidden', !cfg.sizes)
    })
    this.element.querySelectorAll('[data-variant-color-column]').forEach((col) => {
      col.classList.toggle('hidden', !cfg.colors)
    })
  }

  updatePricingSection (slug) {
    const cfg = (slug && this.variantConfigValue[slug]) || { sizes: false, colors: false }
    const hasVariants = Boolean(cfg.sizes || cfg.colors)

    if (this.hasSalePriceLabelTarget) {
      this.salePriceLabelTarget.textContent = hasVariants ? 'Base sale price (PKR)' : 'Sale price (PKR)'
    }
    if (this.hasSalePriceHintTarget) {
      this.salePriceHintTarget.textContent = hasVariants
        ? 'Default price for all options. Only change per option above when a size/colour costs more or less.'
        : ''
    }
    if (this.hasPricingStepHintTarget) {
      this.pricingStepHintTarget.innerHTML = hasVariants
        ? 'Set the <strong class="font-semibold text-slate-800">main price</strong> here. Stock lives in your options above. Cost, discounts, and marketplace settings stay on this step.'
        : 'Enter amounts in <strong class="font-semibold text-slate-800">Pakistani Rupees (PKR)</strong>, e.g. <span class="font-mono text-slate-700">1250</span> or <span class="font-mono text-slate-700">899.50</span>.'
    }
    if (this.hasStockQuantityFieldTarget) {
      this.stockQuantityFieldTarget.classList.toggle('hidden', hasVariants)
    }
    if (this.hasVariantStockNoteTarget) {
      this.variantStockNoteTarget.classList.toggle('hidden', !hasVariants)
    }
  }

  isFieldVisible (key, slug) {
    if (key === 'name') return Boolean(slug)
    if (key === 'product_tags') return Boolean(slug)

    if (!slug) return false

    if (key === 'variant_options') {
      const cfg = this.variantConfigValue[slug]
      return Boolean(cfg && (cfg.sizes || cfg.colors))
    }

    const allowed = this.fieldSlugsValue[key]
    return Boolean(allowed && allowed.split(',').includes(slug))
  }

  currentSlug () {
    if (!this.hasSelectTarget) return 'general'

    const id = this.selectTarget.value
    if (!id) return ''

    return this.slugMapValue[id] || 'general'
  }

  labelFor (key, slug, fallback) {
    const overrides = this.fieldLabelsValue[key]
    if (!overrides) return fallback

    if (overrides[slug]) return overrides[slug]
    return fallback
  }
}
